'use client';

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Key, Plus, Trash2, Copy, AlertCircle, CheckCircle2, Calendar, Clock,
  X, Edit2, Power, PowerOff, Loader2, BookOpen, Terminal, Cpu, ChevronDown,
  ChevronRight, Server, Zap, Shield, User, GraduationCap, Blocks, Link,
  Users, BarChart3, Bot, Presentation, ShoppingBag, Music, FolderOpen, Settings,
  ExternalLink, FileJson, Mic2, TrendingUp, Layers, Wrench, LayoutGrid,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { config } from '@/config';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import {
  API_DOC_PATHS,
  API_EXTRA_ROUTES,
  API_V1_ROUTE_MODULES,
  API_V2_ROUTE_MODULES,
  MCP_HTTP_ENDPOINTS,
  absoluteUrl,
} from '@/lib/api-reference';
import { MCP_RESOURCE_URIS, MCP_CLIENT_ENV_VARS, type McpToolCategory } from '@/lib/mcp-tools-catalog';
import {
  groupOpenApiOperationsByTag,
  parseOpenApiToMcpOperations,
  plainOpenApiDescription,
} from '@/lib/openapi-mcp-tools';
import { filterFormateurApiV1Modules, filterFormateurMcpOperations } from '@/lib/mcp-formateur-scope';
import { isLyrraOne } from '@/lib/platform';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  authMode?: 'client_credentials' | 'header_auth' | string;
  headerName?: string | null;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt?: string | null;
  isActive: boolean;
  key?: string;
  user?: { id: string; email: string; firstName: string | null; lastName: string | null };
}

type NewKeyModalData =
  | { kind: 'client'; name: string; keyPrefix: string; key: string }
  | { kind: 'header'; name: string; headerName: string; secret: string };

interface Teacher {
  id: string; email: string; firstName: string | null; lastName: string | null;
}

// ─── Tool Categories ──────────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  eduflow: GraduationCap,
  blocks: Blocks,
  connections: Link,
  participants: Users,
  progress: TrendingUp,
  analytics: BarChart3,
  ai: Bot,
  presentation: Presentation,
  store: ShoppingBag,
  projects: Music,
  resources: FolderOpen,
  admin: Settings,
  podcasts: Mic2,
  services: Wrench,
  v2: Layers,
};

function categoryIcon(id: string): LucideIcon {
  return CATEGORY_ICONS[id] ?? LayoutGrid;
}

/** Associe un tag OpenAPI à une icône de section (heuristique). */
function openapiTagToIconKey(tag: string): string {
  const tl = tag.toLowerCase();
  if (tl.startsWith('v2/')) return 'v2';
  if (tl === 'eduflow-progress') return 'progress';
  if (tl === 'eduflows') return 'eduflow';
  if (tl === 'participants') return 'participants';
  if (tl === 'analytics') return 'analytics';
  if (tl === 'ai' || tl === 'ai-generation') return 'ai';
  if (tl === 'pptx' || tl === 'pdf' || tl === 'pdf-extractor') return 'presentation';
  if (tl === 'store' || tl === 'stripe') return 'store';
  if (tl === 'projects') return 'projects';
  if (tl === 'podcasts') return 'podcasts';
  if (tl === 'tts' || tl === 'images' || tl === 'user-sounds') return 'services';
  if (tl === 'public') return 'resources';
  if (tl === 'dashboard' || tl === 'admin' || tl.startsWith('institution')) return 'admin';
  if (tl === 'webhooks' || tl === 'support' || tl === 'health') return 'admin';
  return 'connections';
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InstitutionMcpPanel() {
  const { i18n } = useTranslation();
  /** MCP UI and copy are English-only (product requirement), regardless of app language. */
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);
  const formateurScope = isLyrraOne();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<NewKeyModalData | null>(null);
  const [formName, setFormName] = useState('');
  const [formExpiry, setFormExpiry] = useState('');
  const [formUserId, setFormUserId] = useState('');
  const [formAuthMode, setFormAuthMode] = useState<'client_credentials' | 'header_auth'>('client_credentials');
  const [formHeaderName, setFormHeaderName] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'overview' | 'keys' | 'docs'>('overview');
  const [pendingDeleteKeyId, setPendingDeleteKeyId] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState(false);
  const [openApiCategories, setOpenApiCategories] = useState<McpToolCategory[]>([]);
  const [openApiOpCount, setOpenApiOpCount] = useState<number | null>(null);
  const [openApiLoading, setOpenApiLoading] = useState(false);
  const [openApiError, setOpenApiError] = useState<string | null>(null);
  const [mcpToolsFilter, setMcpToolsFilter] = useState('');

  const [formKeyScope, setFormKeyScope] = useState<'mcp' | 'api'>('mcp');

  useEffect(() => {
    void loadApiKeys();
    if (!formateurScope) void loadTeachers();
  }, [formateurScope]);

  useEffect(() => {
    const url = `${config.API_URL.replace(/\/+$/, '')}/openapi.json`;
    let cancelled = false;
    setOpenApiLoading(true);
    setOpenApiError(null);
    fetch(url, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((spec: unknown) => {
        if (cancelled) return;
        const ops = parseOpenApiToMcpOperations(spec);
        const scopedOps = formateurScope ? filterFormateurMcpOperations(ops) : ops;
        setOpenApiOpCount(scopedOps.length);
        setOpenApiCategories(groupOpenApiOperationsByTag(scopedOps));
      })
      .catch(() => {
        if (!cancelled) {
          setOpenApiError(t('institution.mcp.error.openapiLoad'));
          setOpenApiOpCount(null);
          setOpenApiCategories([]);
        }
      })
      .finally(() => {
        if (!cancelled) setOpenApiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, formateurScope]);

  const v1RouteModules = useMemo(
    () => (formateurScope ? filterFormateurApiV1Modules(API_V1_ROUTE_MODULES) : [...API_V1_ROUTE_MODULES]),
    [formateurScope],
  );

  const filteredOpenApiCategories = useMemo(() => {
    const q = mcpToolsFilter.trim().toLowerCase();
    if (!q) return openApiCategories;
    return openApiCategories
      .map((cat) => ({
        ...cat,
        tools: cat.tools.filter(
          (tool) =>
            tool.name.toLowerCase().includes(q) ||
            tool.http.toLowerCase().includes(q) ||
            plainOpenApiDescription(tool.description).toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.tools.length > 0);
  }, [openApiCategories, mcpToolsFilter]);

  const totalToolsDisplay = openApiOpCount ?? '—';

  const loadApiKeys = async () => {
    try {
      setLoading(true); setError(null);
      const endpoint = formateurScope ? '/auth/me/api-keys' : '/institution/api-keys';
      const resp = await api.get<any>(endpoint);
      const raw = resp?.data?.data ?? resp?.data ?? resp;
      const rows = raw?.apiKeys ?? raw?.data?.apiKeys ?? [];
      setApiKeys(Array.isArray(rows) ? rows : []);
    } catch { setApiKeys([]); }
    finally { setLoading(false); }
  };

  const loadTeachers = async () => {
    try {
      const resp = await api.get<any>('/institution/dashboard');
      const raw = resp?.data ?? resp;
      const users = (raw?.users ?? []).map((u: any) => ({ id: u.id, email: u.email, firstName: u.firstName, lastName: u.lastName }));
      setTeachers(users);
    } catch { /* non-blocking */ }
  };

  const handleCreate = async () => {
    if (!formName.trim()) { setError(t('institution.mcp.error.nameRequired')); return; }
    try {
      setError(null);
      const body: Record<string, unknown> = { name: formName.trim(), authMode: formAuthMode };
      if (formExpiry) body.expiresInDays = parseInt(formExpiry, 10);
      if (formateurScope) {
        body.scope = formKeyScope;
      } else if (formUserId) {
        body.forUserId = formUserId;
      }
      if (formAuthMode === 'header_auth' && formHeaderName.trim()) {
        body.headerName = formHeaderName.trim();
      }
      const endpoint = formateurScope ? '/auth/me/api-keys' : '/institution/api-keys';
      const resp = await api.post<any>(endpoint, body);
      const payload = resp?.data?.data ?? resp?.data ?? resp;
      const isHeader = payload?.authMode === 'header_auth' && typeof payload?.secret === 'string';
      if (isHeader) {
        setNewKeyData({
          kind: 'header',
          name: payload.name,
          headerName: payload.headerName || 'X-Lyrra-Api-Key',
          secret: payload.secret,
        });
      } else {
        setNewKeyData({
          kind: 'client',
          name: payload.name,
          keyPrefix: payload.keyPrefix,
          key: payload.key,
        });
      }
      setShowCreateModal(false);
      setShowNewKeyModal(true);
      setFormName('');
      setFormExpiry('');
      setFormUserId('');
      setFormAuthMode('client_credentials');
      setFormHeaderName('');
      setFormKeyScope('mcp');
      await loadApiKeys();
    } catch (err: any) { setError(err?.response?.data?.error || t('institution.mcp.error.create')); }
  };

  const handleDelete = async (id: string) => {
    setPendingDeleteKeyId(id);
  };

  const confirmDeleteKey = async () => {
    if (!pendingDeleteKeyId) return;
    try {
      setDeletingKey(true);
      const endpoint = formateurScope
        ? `/auth/me/api-keys/${pendingDeleteKeyId}`
        : `/institution/api-keys/${pendingDeleteKeyId}`;
      await api.delete(endpoint);
      setPendingDeleteKeyId(null);
      await loadApiKeys();
    } catch {
      setError(t('institution.mcp.error.delete'));
    } finally {
      setDeletingKey(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      const endpoint = formateurScope
        ? `/auth/me/api-keys/${id}/toggle`
        : `/institution/api-keys/${id}/toggle`;
      await api.patch(endpoint);
      await loadApiKeys();
    } catch { setError(t('institution.mcp.error.toggle')); }
  };

  const handleUpdateName = async (id: string) => {
    if (!editName.trim() || formateurScope) return;
    try { await api.patch(`/institution/api-keys/${id}`, { name: editName.trim() }); setEditingKey(null); setEditName(''); await loadApiKeys(); } catch { setError(t('institution.mcp.error.update')); }
  };

  const copyToClipboard = (value: string, id: string) => { navigator.clipboard.writeText(value); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };

  const formatDate = (d: string | null) =>
    d
      ? new Date(d).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('institution.mcp.never');

  const claudeConfigSnippet = useMemo(() => {
    const cid = t('institution.mcp.config.clientIdPlaceholder');
    const sec = t('institution.mcp.config.clientSecretPlaceholder');
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return JSON.stringify(
      {
        mcpServers: {
          'lyrra-studio': {
            command: 'npx',
            args: ['-y', '@lyrra/mcp-server', 'lyrra-mcp'],
            env: {
              LYRRA_CLIENT_ID: cid,
              LYRRA_CLIENT_SECRET: sec,
              LYRRA_API_URL: `${origin}/api`,
            },
          },
        },
      },
      null,
      2,
    );
  }, [t]);

  const claudeWebSteps = useMemo(
    () => [
      t('institution.mcp.claudeWeb.step1'),
      t('institution.mcp.claudeWeb.step2'),
      t('institution.mcp.claudeWeb.step3'),
      t('institution.mcp.claudeWeb.step4'),
      t('institution.mcp.claudeWeb.step5'),
      t('institution.mcp.claudeWeb.step6'),
    ],
    [t],
  );
  const isExpired = (d: string | null) => d ? new Date(d) < new Date() : false;
  const isExpiringSoon = (d: string | null) => { if (!d) return false; const days = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000); return days <= 7 && days > 0; };
  const toggleCategory = (id: string) => setExpandedCategories((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const userName = (u?: { firstName: string | null; lastName: string | null; email: string }) => u ? ((u.firstName || u.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : u.email) : '';

  const mcpDesignerSelectOptions = useMemo(
    () => [
      { value: '', label: t('institution.mcp.keys.modal.selectDesigner') },
      ...teachers.map((teacher) => ({
        value: teacher.id,
        label: `${userName(teacher)} (${teacher.email})`,
        icon: User,
      })),
    ],
    [teachers, t],
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#ecf8f6] to-[#f4fbfa] dark:from-[#0C1C38]/20 dark:to-[#0C1C38]/10 rounded-2xl border border-[#0C1C38]/20 dark:border-[#0C1C38]/30 p-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#0C1C38] rounded-xl flex items-center justify-center">
              <Cpu className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0C1C38] dark:text-white">{t('institution.mcp.title')}</h1>
              <p className="text-[#5B6472] dark:text-[#B6CAEC] text-sm">{t('institution.mcp.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white dark:bg-[#122644] rounded-lg px-4 py-2 text-center border border-[#0C1C38]/30 dark:border-[#0C1C38]/40">
              <div className="text-2xl font-bold text-[#0C1C38] dark:text-white">{totalToolsDisplay}</div>
              <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.mcp.stats.toolsAvailable')}</div>
            </div>
            <div className="bg-white dark:bg-[#122644] rounded-lg px-4 py-2 text-center border border-[#0C1C38]/30 dark:border-[#0C1C38]/40">
              <div className="text-2xl font-bold text-[#0C1C38] dark:text-white">{apiKeys.filter((k) => k.isActive && !isExpired(k.expiresAt)).length}</div>
              <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.mcp.stats.activeKeys')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E8EDF5] dark:border-[#19335E]">
        {(
          [
            ['overview', 'institution.mcp.tab.overview', Server],
            ['keys', 'institution.mcp.tab.keys', Key],
            ['docs', 'institution.mcp.tab.docs', BookOpen],
          ] as const
        ).map(([tab, labelKey, Icon]) => (
          <button key={tab} type="button" onClick={() => setActiveTab(tab)} className={cn('px-5 py-3 text-sm font-medium rounded-t-lg transition-all flex items-center gap-2', activeTab === tab ? 'bg-[#0C1C38] text-white shadow-sm' : 'text-[#5B6472] hover:text-[#0C1C38] hover:bg-[#F8FAFC] dark:text-[#B6CAEC] dark:hover:text-white dark:hover:bg-[#122644]')}>
            <Icon className="w-4 h-4" />{t(labelKey)}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
          <button type="button" onClick={() => setError(null)} className="ms-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* What is MCP */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-[#0C1C38]" /> {t('institution.mcp.whatIs.title')}</h2>
            <p className="text-[#5B6472] dark:text-[#DCE7F7] text-sm mb-4">{t('institution.mcp.whatIs.body')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {([
                { nameKey: 'institution.mcp.clients.claude.name', descKey: 'institution.mcp.clients.claude.desc' },
                { nameKey: 'institution.mcp.clients.cursor.name', descKey: 'institution.mcp.clients.cursor.desc' },
                { nameKey: 'institution.mcp.clients.copilot.name', descKey: 'institution.mcp.clients.copilot.desc' },
              ] as const).map((tool) => (
                <div key={tool.nameKey} className="bg-[#F8FAFC] dark:bg-[#19335E]/50 rounded-lg p-4 border border-[#E8EDF5] dark:border-[#B6CAEC]">
                  <div className="font-semibold text-[#0C1C38] dark:text-white text-sm mb-1">{t(tool.nameKey)}</div>
                  <div className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t(tool.descKey)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Start */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><Terminal className="w-5 h-5 text-[#19335E]" /> {t('institution.mcp.quickStart.title')}</h2>
            <div className="space-y-4">
              {([
                { n: '1', titleKey: 'institution.mcp.quickStart.step1Title', descKey: 'institution.mcp.quickStart.step1Desc' },
                { n: '2', titleKey: 'institution.mcp.quickStart.step2Title', descKey: 'institution.mcp.quickStart.step2Desc' },
                { n: '3', titleKey: 'institution.mcp.quickStart.step3Title', descKey: 'institution.mcp.quickStart.step3Desc' },
              ] as const).map((s) => (
                <div key={s.n} className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#0C1C38]/15 dark:bg-[#0C1C38]/25 rounded-full flex items-center justify-center text-[#0C1C38] dark:text-[#6fd0c3] font-bold text-sm shrink-0">{s.n}</div>
                  <div><div className="font-medium text-[#0C1C38] dark:text-white text-sm">{t(s.titleKey)}</div><div className="text-xs text-[#5B6472] dark:text-[#B6CAEC]">{t(s.descKey)}</div></div>
                </div>
              ))}
            </div>
          </div>

          {/* Server URLs */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><Server className="w-5 h-5 text-blue-500" /> {t('institution.mcp.serverUrls.title')}</h2>
            <div className="space-y-3">
              {([
                { labelKey: 'institution.mcp.serverUrls.labelApi', value: `${window.location.origin}/api`, id: 'api-url' },
                { labelKey: 'institution.mcp.serverUrls.labelMcp', value: `${window.location.origin}/mcp`, id: 'mcp-url' },
              ] as const).map((u) => (
                <div key={u.id} className="flex items-center justify-between bg-[#F8FAFC] dark:bg-[#19335E]/50 rounded-lg p-3 border border-[#E8EDF5] dark:border-[#B6CAEC]">
                  <div><div className="text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t(u.labelKey)}</div><code className="text-sm font-mono text-[#0C1C38] dark:text-[#6fd0c3]">{u.value}</code></div>
                  <button type="button" onClick={() => copyToClipboard(u.value, u.id)} className="p-2 hover:bg-[#E8EDF5] dark:hover:bg-[#B6CAEC] rounded-lg transition-colors">
                    {copiedId === u.id ? <CheckCircle2 className="w-4 h-4 text-[#19335E]" /> : <Copy className="w-4 h-4 text-[#5B6472]/70" />}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mt-3 leading-relaxed">
              {t('institution.mcp.serverUrls.mcpHttpNote')}
            </p>
            <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mt-3">
              <button type="button" onClick={() => setActiveTab('docs')} className="font-medium text-[#0C1C38] hover:underline">
                {t('institution.mcp.serverUrls.docTab')}
              </button>
              {' · '}
              <a
                href={absoluteUrl(API_DOC_PATHS.swagger)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-[#0C1C38] hover:underline inline-flex items-center gap-1"
              >
                {t('institution.mcp.serverUrls.swaggerUi')} <ExternalLink className="w-3 h-3" />
              </a>
              {t('institution.mcp.serverUrls.swaggerSuffix')}
            </p>
          </div>

          {/* Claude Desktop Config */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><Cpu className="w-5 h-5 text-[#0C1C38]" /> {t('institution.mcp.claudeDesktop.title')}</h2>
            <p className="text-xs text-[#5B6472]/70 mb-2 italic">{t('institution.mcp.claudeDesktop.fileHint')}</p>
            <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mb-2">{t('institution.mcp.claudeDesktop.npxIntro')}</p>
            <div className="relative">
              <pre className="bg-[#F8FAFC] dark:bg-[#0C1C38] text-[#121826] dark:text-[#DCE7F7] rounded-lg p-4 text-sm font-mono overflow-x-auto whitespace-pre border border-[#E8EDF5] dark:border-[#19335E]">{claudeConfigSnippet}</pre>
              <button type="button" onClick={() => copyToClipboard(claudeConfigSnippet, 'claude-config')} className="absolute top-3 end-3 p-2 bg-white dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded-lg transition shadow-sm" title={t('institution.mcp.copyConfig')}>
                {copiedId === 'claude-config' ? <CheckCircle2 className="w-4 h-4 text-[#19335E]" /> : <Copy className="w-4 h-4 text-[#5B6472]/70" />}
              </button>
            </div>
            <p className="text-xs text-[#5B6472]/70 mt-3">
              {t('institution.mcp.claudeDesktop.footnote')}
            </p>
          </div>

          {/* Claude.ai Web */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> {t('institution.mcp.claudeWeb.title')}</h2>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mb-4">{t('institution.mcp.claudeWeb.intro')}</p>
            <div className="space-y-3 mb-4">
              {claudeWebSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-[#0C1C38]/15 dark:bg-[#0C1C38]/25 text-[#0C1C38] dark:text-[#6fd0c3] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <p className="text-sm text-[#5B6472] dark:text-[#DCE7F7]">{step}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between bg-[#F8FAFC] dark:bg-[#19335E]/50 rounded-lg p-3 border border-[#E8EDF5] dark:border-[#B6CAEC]">
              <div><div className="text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC]">{t('institution.mcp.claudeWeb.mcpUrlLabel')}</div><code className="text-sm font-mono text-[#0C1C38] dark:text-[#6fd0c3]">{window.location.origin}/mcp</code></div>
              <button type="button" onClick={() => copyToClipboard(`${window.location.origin}/mcp`, 'web-mcp-url')} className="p-2 hover:bg-[#E8EDF5] dark:hover:bg-[#B6CAEC] rounded-lg transition-colors">
                {copiedId === 'web-mcp-url' ? <CheckCircle2 className="w-4 h-4 text-[#19335E]" /> : <Copy className="w-4 h-4 text-[#5B6472]/70" />}
              </button>
            </div>
            <p className="text-xs text-[#5B6472]/70 mt-4 italic">{t('institution.mcp.claudeWeb.oauthNote')}</p>
          </div>
        </div>
      )}

      {/* ═══ API KEYS TAB ═══ */}
      {activeTab === 'keys' && (
        <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white">{t('institution.mcp.keys.title')}</h2>
              <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                {formateurScope
                  ? 'Personal API keys for MCP and REST integrations (your account only).'
                  : t('institution.mcp.keys.subtitle')}
              </p>
            </div>
            <button type="button" onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#0C1C38] hover:bg-[#23867a] text-white text-sm font-medium rounded-lg transition-all">
              <Plus className="w-4 h-4" /> {t('institution.mcp.keys.newKey')}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0C1C38]" /></div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="w-16 h-16 text-[#5B6472]/50 dark:text-[#B6CAEC] mx-auto mb-4" />
              <p className="text-[#5B6472] dark:text-[#B6CAEC] mb-4">{t('institution.mcp.keys.empty')}</p>
              <button type="button" onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-[#0C1C38] hover:bg-[#23867a] text-white rounded-lg transition-all text-sm">{t('institution.mcp.keys.createFirst')}</button>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div key={key.id} className={cn('p-4 border rounded-xl transition-all', !key.isActive || isExpired(key.expiresAt) ? 'bg-[#F8FAFC] dark:bg-[#122644]/50 border-[#E8EDF5] dark:border-[#19335E] opacity-60' : 'bg-white dark:bg-[#122644] border-[#E8EDF5] dark:border-[#19335E]')}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        {editingKey === key.id && !formateurScope ? (
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="px-3 py-1 border border-[#E8EDF5] dark:border-[#19335E] rounded-lg bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white flex-1 min-w-0 focus:ring-2 focus:ring-[#0C1C38]/30 text-sm" autoFocus />
                            <button type="button" onClick={() => handleUpdateName(key.id)} className="px-3 py-1 bg-[#0C1C38] text-white rounded-lg hover:bg-[#23867a] text-xs">{t('institution.mcp.keys.editOk')}</button>
                            <button type="button" onClick={() => { setEditingKey(null); setEditName(''); }} className="px-3 py-1 bg-[#F0F4FA] dark:bg-[#19335E] text-[#5B6472] dark:text-[#DCE7F7] rounded-lg text-xs">{t('btn.cancel')}</button>
                          </div>
                        ) : (
                          <>
                            <h3 className="text-base font-semibold text-[#0C1C38] dark:text-white">{key.name}</h3>
                            {!formateurScope ? (
                              <button type="button" onClick={() => { setEditingKey(key.id); setEditName(key.name); }} className="p-1 text-[#5B6472]/70 hover:text-[#0C1C38]"><Edit2 className="w-3.5 h-3.5" /></button>
                            ) : null}
                          </>
                        )}
                        {key.isActive && !isExpired(key.expiresAt) && <span className="px-2 py-0.5 bg-[#F0F4FA] text-[#0C1C38] dark:bg-[#F8FAFC]/30 dark:text-[#5C82C5] border border-[#B6CAEC] dark:border-[#F0F4FA] text-xs font-medium rounded">{t('institution.mcp.keys.status.active')}</span>}
                        {!key.isActive && <span className="px-2 py-0.5 bg-[#F0F4FA] text-[#5B6472] dark:bg-[#19335E] dark:text-[#B6CAEC] border border-[#E8EDF5] dark:border-[#B6CAEC] text-xs font-medium rounded">{t('institution.mcp.keys.status.inactive')}</span>}
                        {isExpired(key.expiresAt) && <span className="px-2 py-0.5 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800 text-xs font-medium rounded">{t('institution.mcp.keys.status.expired')}</span>}
                        {isExpiringSoon(key.expiresAt) && !isExpired(key.expiresAt) && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-xs font-medium rounded">{t('institution.mcp.keys.status.expiringSoon')}</span>}
                        {key.authMode === 'header_auth' ? (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-200 dark:border-blue-800 text-xs font-medium rounded">{t('institution.mcp.keys.badge.header')}</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-[#F0F4FA] text-[#5B6472] dark:bg-[#19335E] dark:text-[#DCE7F7] border border-[#E8EDF5] dark:border-[#B6CAEC] text-xs font-medium rounded">{t('institution.mcp.keys.badge.client')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                        <Shield className="w-3.5 h-3.5" /><span className="text-xs text-[#5B6472]/70">{t('institution.mcp.keys.clientId')}</span>
                        <code className="font-mono bg-[#F8FAFC] dark:bg-[#19335E] border border-[#E8EDF5] dark:border-[#B6CAEC] px-2 py-0.5 rounded text-[#121826] dark:text-[#DCE7F7] text-xs">{key.keyPrefix}</code>
                        <button type="button" onClick={() => copyToClipboard(key.keyPrefix, `pfx-${key.id}`)} className="p-0.5 hover:text-[#0C1C38]">
                          {copiedId === `pfx-${key.id}` ? <CheckCircle2 className="w-3.5 h-3.5 text-[#19335E]" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {key.authMode === 'header_auth' && key.headerName && (
                        <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                          <Link className="w-3.5 h-3.5" /><span className="text-xs text-[#5B6472]/70">{t('institution.mcp.keys.headerNameList')}</span>
                          <code className="font-mono bg-[#F8FAFC] dark:bg-[#19335E] border border-[#E8EDF5] dark:border-[#B6CAEC] px-2 py-0.5 rounded text-[#121826] dark:text-[#DCE7F7] text-xs">{key.headerName}</code>
                          <button type="button" onClick={() => copyToClipboard(key.headerName!, `hn-${key.id}`)} className="p-0.5 hover:text-[#0C1C38]">
                            {copiedId === `hn-${key.id}` ? <CheckCircle2 className="w-3.5 h-3.5 text-[#19335E]" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                        <Key className="w-3.5 h-3.5" /><span className="text-xs text-[#5B6472]/70">{key.authMode === 'header_auth' ? t('institution.mcp.keys.modal.fieldHeaderValue') : t('institution.mcp.keys.clientSecret')}</span>
                        <code className="font-mono bg-[#F8FAFC] dark:bg-[#19335E] border border-[#E8EDF5] dark:border-[#B6CAEC] px-2 py-0.5 rounded text-[#5B6472]/70 italic text-xs">••••••••••••••••</code>
                        <span className="text-xs text-amber-600 dark:text-amber-400">{t('institution.mcp.keys.visibleOnce')}</span>
                      </div>
                      {key.user && !formateurScope && (
                        <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#B6CAEC] mt-1">
                          <User className="w-3.5 h-3.5" /><span className="text-xs text-[#5B6472]/70">{t('institution.mcp.keys.designer')}</span>
                          <span className="font-medium text-[#0C1C38] dark:text-white text-xs">{userName(key.user)}</span>
                          {(key.user.firstName || key.user.lastName) && <span className="text-xs text-[#5B6472]/70">({key.user.email})</span>}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#5B6472] dark:text-[#B6CAEC]">
                        <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {t('institution.mcp.keys.created')} {formatDate(key.createdAt)}</div>
                        <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t('institution.mcp.keys.used')} {formatDate(key.lastUsedAt ?? null)}</div>
                        {key.expiresAt && <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t('institution.mcp.keys.expires')} {formatDate(key.expiresAt)}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ms-4 shrink-0">
                      <button type="button" onClick={() => handleToggle(key.id)} className="p-2 hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] rounded-lg transition-colors" title={key.isActive ? t('institution.mcp.keys.disable') : t('institution.mcp.keys.enable')}>
                        {key.isActive ? <PowerOff className="w-4 h-4 text-[#5B6472] dark:text-[#B6CAEC]" /> : <Power className="w-4 h-4 text-[#19335E]" />}
                      </button>
                      <button type="button" onClick={() => handleDelete(key.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title={t('institution.mcp.keys.delete')}><Trash2 className="w-4 h-4 text-red-600" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ DOCUMENTATION TAB ═══ */}
      {activeTab === 'docs' && (
        <div className="space-y-4">
          {/* Référence API REST (OpenAPI) */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white mb-2 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-[#0C1C38]" />
              {t('institution.mcp.docs.apiRestTitle')}
            </h2>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mb-4">
              {t('institution.mcp.docs.apiRestDesc')}
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              <a
                href={absoluteUrl(API_DOC_PATHS.swagger)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-[#0C1C38] px-4 py-2 text-sm font-medium text-white hover:bg-[#23867a] transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {t('institution.mcp.docs.openSwagger')}
              </a>
              <a
                href={absoluteUrl(API_DOC_PATHS.openapiJson)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] bg-[#F8FAFC] dark:bg-[#19335E]/50 px-4 py-2 text-sm font-medium text-[#0C1C38] dark:text-[#DCE7F7] hover:bg-[#F0F4FA] dark:hover:bg-[#19335E] transition-colors"
              >
                <FileJson className="w-4 h-4" />
                {t('institution.mcp.docs.openapiJson')}
              </a>
              <a
                href={absoluteUrl(API_DOC_PATHS.health)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] px-4 py-2 text-sm text-[#5B6472] dark:text-[#DCE7F7] hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50"
              >
                <ExternalLink className="w-4 h-4" />
                {t('institution.mcp.docs.healthLink')}
              </a>
            </div>
            <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white mb-2">{t('institution.mcp.docs.v1Title')}</h3>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] mb-4">
              <table className="w-full text-xs">
                <caption className="sr-only">{t('institution.mcp.docs.captionV1')}</caption>
                <thead>
                  <tr className="bg-[#F8FAFC] dark:bg-[#19335E]/50 text-start text-[#5B6472] dark:text-[#B6CAEC]">
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tablePrefix')}</th>
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tableRole')}</th>
                  </tr>
                </thead>
                <tbody>
                  {v1RouteModules.map((m, i) => (
                    <tr key={m.prefix} className={i % 2 === 0 ? 'bg-white dark:bg-[#122644]' : 'bg-[#F8FAFC]/80 dark:bg-[#19335E]/20'}>
                      <td className="px-3 py-1.5 font-mono text-[#0C1C38] dark:text-[#6fd0c3] whitespace-nowrap">{m.prefix}</td>
                      <td className="px-3 py-1.5 text-[#5B6472] dark:text-[#DCE7F7]">{t(m.i18nKey)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!formateurScope ? (
              <>
            <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white mb-2">{t('institution.mcp.docs.v2Title')}</h3>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] mb-4">
              <table className="w-full text-xs">
                <caption className="sr-only">{t('institution.mcp.docs.captionV2')}</caption>
                <thead>
                  <tr className="bg-[#F8FAFC] dark:bg-[#19335E]/50 text-start text-[#5B6472] dark:text-[#B6CAEC]">
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tablePrefix')}</th>
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tableRole')}</th>
                  </tr>
                </thead>
                <tbody>
                  {API_V2_ROUTE_MODULES.map((m, i) => (
                    <tr key={m.prefix} className={i % 2 === 0 ? 'bg-white dark:bg-[#122644]' : 'bg-[#F8FAFC]/80 dark:bg-[#19335E]/20'}>
                      <td className="px-3 py-1.5 font-mono text-blue-700 dark:text-blue-300 whitespace-nowrap">{m.prefix}</td>
                      <td className="px-3 py-1.5 text-[#5B6472] dark:text-[#DCE7F7]">{t(m.i18nKey)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white mb-2">{t('institution.mcp.docs.extraRoutesTitle')}</h3>
            <ul className="text-xs text-[#5B6472] dark:text-[#DCE7F7] space-y-1 mb-4 list-disc list-inside">
              {API_EXTRA_ROUTES.map((r) => (
                <li key={r.path}>
                  <code className="font-mono text-[#0C1C38] dark:text-[#6fd0c3]">{r.method}</code>{' '}
                  <code className="font-mono text-[#121826] dark:text-[#DCE7F7]">{r.path}</code> — {t(r.i18nKey)}
                </li>
              ))}
            </ul>
            <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-white mb-2">{t('institution.mcp.docs.keyEndpointsTitle')}</h3>
            <div className="rounded-lg bg-[#F8FAFC] dark:bg-[#0C1C38]/40 border border-[#E8EDF5] dark:border-[#B6CAEC] p-3 text-xs space-y-2 text-[#5B6472] dark:text-[#DCE7F7]">
              {!formateurScope ? (
                <>
              <p className="font-medium text-[#0C1C38] dark:text-[#DCE7F7]">{t('institution.mcp.docs.sectionInstitutionKeys')}</p>
              <ul className="font-mono space-y-0.5 list-disc list-inside ms-1">
                <li>{MCP_HTTP_ENDPOINTS.institutionKeys.list}</li>
                <li>{MCP_HTTP_ENDPOINTS.institutionKeys.create}</li>
                <li>{MCP_HTTP_ENDPOINTS.institutionKeys.updateName}</li>
                <li>{MCP_HTTP_ENDPOINTS.institutionKeys.toggle}</li>
                <li>{MCP_HTTP_ENDPOINTS.institutionKeys.remove}</li>
              </ul>
                </>
              ) : null}
              <p className={cn('font-medium text-[#0C1C38] dark:text-[#DCE7F7]', !formateurScope && 'pt-2')}>{t('institution.mcp.docs.sectionUserKeys')}</p>
              <ul className="font-mono space-y-0.5 list-disc list-inside ms-1">
                <li>{MCP_HTTP_ENDPOINTS.userKeys.list}</li>
                <li>{MCP_HTTP_ENDPOINTS.userKeys.create}</li>
                <li>{MCP_HTTP_ENDPOINTS.userKeys.toggle}</li>
                <li>{MCP_HTTP_ENDPOINTS.userKeys.remove}</li>
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h2 className="text-xl font-bold text-[#0C1C38] dark:text-white mb-2">{t('institution.mcp.docs.toolsTitle')}</h2>
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] mb-2">
              {t('institution.mcp.docs.toolsP1')}
            </p>
            <p className="text-xs text-[#0C1C38] dark:text-[#19335E]/90 bg-[#F0F4FA] dark:bg-[#F8FAFC]/30 border border-[#B6CAEC] dark:border-[#F8FAFC]/40 rounded-lg px-3 py-2 mb-4">
              {t('institution.mcp.docs.toolsCallout')}{' '}
              <a href={absoluteUrl(API_DOC_PATHS.swagger)} className="underline font-medium" target="_blank" rel="noopener noreferrer">
                Swagger
              </a>
              .
            </p>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="px-3 py-1 bg-[#0C1C38]/10 dark:bg-[#0C1C38]/20 text-[#0C1C38] dark:text-[#6fd0c3] border border-[#0C1C38]/30 dark:border-[#0C1C38]/40 rounded-full text-sm font-medium">
                {openApiLoading ? '…' : t('institution.mcp.docs.badgeOpenapiOps', { count: openApiOpCount ?? '—' })}
              </span>
              <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-full text-sm font-medium">{t('institution.mcp.docs.badgeResources', { count: MCP_RESOURCE_URIS.length })}</span>
              <span className="px-3 py-1 bg-[#F0F4FA] dark:bg-[#F8FAFC]/30 text-[#0C1C38] dark:text-[#5C82C5] border border-[#B6CAEC] dark:border-[#DCE7F7] rounded-full text-sm font-medium">
                {openApiLoading ? '…' : t('institution.mcp.docs.badgeModules', { count: openApiCategories.length })}
              </span>
            </div>
            {openApiError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">{openApiError}</p>
            )}
            <label className="block text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC] mb-1.5" htmlFor="mcp-tools-filter">
              {t('institution.mcp.docs.filterLabel')}
            </label>
            <input
              id="mcp-tools-filter"
              type="search"
              value={mcpToolsFilter}
              onChange={(e) => setMcpToolsFilter(e.target.value)}
              placeholder={t('institution.mcp.docs.filterPlaceholder')}
              className="w-full max-w-xl px-3 py-2 text-sm rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC] bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white"
            />
          </div>

          {/* Variables MCP client */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h3 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-3 flex items-center gap-2"><Terminal className="w-5 h-5 text-[#0C1C38]" /> {t('institution.mcp.docs.envTitle')}</h3>
            <div className="overflow-x-auto rounded-lg border border-[#E8EDF5] dark:border-[#B6CAEC]">
              <table className="w-full text-xs">
                <caption className="sr-only">{t('institution.mcp.docs.envCaption')}</caption>
                <thead>
                  <tr className="bg-[#F8FAFC] dark:bg-[#19335E]/50 text-start text-[#5B6472] dark:text-[#B6CAEC]">
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tableVariable')}</th>
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tableExample')}</th>
                    <th scope="col" className="px-3 py-2 font-medium">{t('institution.mcp.docs.tableNote')}</th>
                  </tr>
                </thead>
                <tbody>
                  {MCP_CLIENT_ENV_VARS.map((row, i) => (
                    <tr key={row.key} className={i % 2 === 0 ? 'bg-white dark:bg-[#122644]' : 'bg-[#F8FAFC] dark:bg-[#19335E]/20'}>
                      <td className="px-3 py-2 font-mono text-[#0C1C38] dark:text-[#6fd0c3] whitespace-nowrap">{row.key}</td>
                      <td className="px-3 py-2 font-mono text-[#121826] dark:text-[#DCE7F7]">{t(row.exampleKey)}</td>
                      <td className="px-3 py-2 text-[#5B6472] dark:text-[#DCE7F7]">{t(row.noteKey)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Resources */}
          <div className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] p-6">
            <h3 className="text-lg font-bold text-[#0C1C38] dark:text-white mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-blue-500" /> {t('institution.mcp.docs.resourcesTitle')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {MCP_RESOURCE_URIS.map((r) => (
                <div key={r.uri} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <code className="text-sm font-mono text-blue-700 dark:text-blue-300">{r.uri}</code>
                  <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mt-1">{t(r.descriptionKey)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Outils = opérations OpenAPI (par tag) */}
          {openApiLoading && (
            <div className="flex items-center gap-2 text-sm text-[#5B6472] dark:text-[#B6CAEC] py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('institution.mcp.docs.loadingCatalog')}
            </div>
          )}
          {!openApiLoading && filteredOpenApiCategories.length === 0 && !openApiError && (
            <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC] py-4">{t('institution.mcp.docs.noFilterMatch')}</p>
          )}
          {filteredOpenApiCategories.map((category) => {
            const iconKey = openapiTagToIconKey(category.openApiTag ?? category.id);
            const Icon = categoryIcon(iconKey);
            return (
              <div key={category.id} className="bg-white dark:bg-[#122644] rounded-xl border border-[#E8EDF5] dark:border-[#19335E] overflow-hidden">
                <button type="button" onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between p-4 hover:bg-[#F8FAFC] dark:hover:bg-[#19335E]/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-[#0C1C38]" />
                    <span className="font-semibold text-[#0C1C38] dark:text-white text-sm">{category.title}</span>
                    <span className="px-2 py-0.5 bg-[#F0F4FA] dark:bg-[#19335E] text-[#5B6472] dark:text-[#B6CAEC] border border-[#E8EDF5] dark:border-[#B6CAEC] rounded text-xs">{t('institution.mcp.docs.operationsCount', { count: category.tools.length })}</span>
                  </div>
                  {expandedCategories.has(category.id) ? <ChevronDown className="w-4 h-4 text-[#5B6472]/70" /> : <ChevronRight className="w-4 h-4 text-[#5B6472]/70" />}
                </button>
                {expandedCategories.has(category.id) && (
                  <div className="border-t border-[#E8EDF5] dark:border-[#19335E] overflow-x-auto">
                    <table className="w-full min-w-[720px]">
                      <caption className="sr-only">{t('institution.mcp.docs.captionTools')}</caption>
                      <thead>
                        <tr className="bg-[#F8FAFC] dark:bg-[#19335E]/50">
                          <th scope="col" className="px-4 py-2 text-start text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC] uppercase w-[14rem]">{t('institution.mcp.docs.tableTool')}</th>
                          <th scope="col" className="px-4 py-2 text-start text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC] uppercase">{t('institution.mcp.docs.tableDescription')}</th>
                          <th scope="col" className="px-4 py-2 text-start text-xs font-medium text-[#5B6472] dark:text-[#B6CAEC] uppercase w-[12rem]">{t('institution.mcp.docs.tableHttp')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {category.tools.map((tool, i) => (
                          <tr key={`${category.id}-${tool.http}-${tool.name}`} className={i % 2 === 0 ? 'bg-white dark:bg-[#122644]' : 'bg-[#F8FAFC] dark:bg-[#19335E]/30'}>
                            <td className="px-4 py-2 align-top"><code className="text-xs font-mono text-[#0C1C38] dark:text-[#6fd0c3] break-all">{tool.name}</code></td>
                            <td className="px-4 py-2 text-xs text-[#5B6472] dark:text-[#DCE7F7] align-top">
                              <div className="max-h-48 overflow-y-auto whitespace-pre-wrap">{plainOpenApiDescription(tool.description)}</div>
                            </td>
                            <td className="px-4 py-2 text-xs font-mono text-[#121826] dark:text-[#DCE7F7] align-top break-all">{tool.http}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ CREATE KEY MODAL ═══ */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#0C1C38] rounded-2xl max-w-md w-full p-6 border border-[#E8EDF5] dark:border-[#19335E] shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-[#0C1C38] dark:text-white">{t('institution.mcp.keys.modal.createTitle')}</h3>
              <button type="button" onClick={() => { setShowCreateModal(false); setFormName(''); setFormExpiry(''); setFormUserId(''); setFormAuthMode('client_credentials'); setFormHeaderName(''); setFormKeyScope('mcp'); }} className="p-1 hover:bg-[#F0F4FA] dark:hover:bg-[#122644] rounded-lg"><X className="w-5 h-5 text-[#5B6472]/70" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <span className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-2">{t('institution.mcp.keys.modal.authTypeLabel')}</span>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer text-sm text-[#121826] dark:text-[#DCE7F7]">
                    <input type="radio" name="mcp-auth" checked={formAuthMode === 'client_credentials'} onChange={() => setFormAuthMode('client_credentials')} className="mt-1" />
                    <span>{t('institution.mcp.keys.modal.authClient')}</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm text-[#121826] dark:text-[#DCE7F7]">
                    <input type="radio" name="mcp-auth" checked={formAuthMode === 'header_auth'} onChange={() => setFormAuthMode('header_auth')} className="mt-1" />
                    <span>{t('institution.mcp.keys.modal.authHeader')}</span>
                  </label>
                </div>
              </div>
              {formAuthMode === 'header_auth' && (
                <div>
                  <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.headerNameLabel')}</label>
                  <input type="text" value={formHeaderName} onChange={(e) => setFormHeaderName(e.target.value)} className="w-full px-4 py-2 border border-[#E8EDF5] dark:border-[#19335E] rounded-lg bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white focus:ring-2 focus:ring-[#0C1C38]/30 text-sm font-mono" placeholder={t('institution.mcp.keys.modal.headerNamePlaceholder')} maxLength={64} />
                  <p className="text-xs text-[#5B6472]/70 mt-1">{t('institution.mcp.keys.modal.headerNameHint')}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.nameLabel')}</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} className="w-full px-4 py-2 border border-[#E8EDF5] dark:border-[#19335E] rounded-lg bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white focus:ring-2 focus:ring-[#0C1C38]/30 text-sm" placeholder={t('institution.mcp.keys.modal.namePlaceholder')} maxLength={100} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.expiryLabel')}</label>
                <input type="number" value={formExpiry} onChange={(e) => setFormExpiry(e.target.value)} className="w-full px-4 py-2 border border-[#E8EDF5] dark:border-[#19335E] rounded-lg bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white focus:ring-2 focus:ring-[#0C1C38]/30 text-sm" placeholder={t('institution.mcp.keys.modal.expiryPlaceholder')} min="1" />
              </div>
              {formateurScope ? (
                <div>
                  <label className="block text-sm font-medium text-[#121826] dark:text-[#DCE7F7] mb-1.5" htmlFor="mcp-key-scope">
                    Scope
                  </label>
                  <select
                    id="mcp-key-scope"
                    value={formKeyScope}
                    onChange={(e) => setFormKeyScope(e.target.value as 'mcp' | 'api')}
                    className="w-full px-4 py-2 border border-[#E8EDF5] dark:border-[#19335E] rounded-lg bg-white dark:bg-[#122644] text-[#0C1C38] dark:text-white focus:ring-2 focus:ring-[#0C1C38]/30 text-sm"
                  >
                    <option value="mcp">MCP</option>
                    <option value="api">API</option>
                  </select>
                </div>
              ) : null}
              {teachers.length > 0 && !formateurScope && (
                <div>
                  <InstitutionStyledSelect
                    panelZIndexClass="z-[200]"
                    label={t('institution.mcp.keys.modal.designerLabel')}
                    hint={t('institution.mcp.keys.modal.designerHint')}
                    options={mcpDesignerSelectOptions}
                    value={formUserId}
                    onChange={setFormUserId}
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4 border-t border-[#E8EDF5] dark:border-[#19335E]">
                <button type="button" onClick={handleCreate} className="flex-1 px-4 py-2.5 bg-[#0C1C38] hover:bg-[#23867a] text-white text-sm font-medium rounded-lg transition-all">{t('btn.create')}</button>
                <button type="button" onClick={() => { setShowCreateModal(false); setFormName(''); setFormExpiry(''); setFormUserId(''); setFormAuthMode('client_credentials'); setFormHeaderName(''); setFormKeyScope('mcp'); }} className="px-4 py-2.5 bg-[#F0F4FA] dark:bg-[#122644] text-[#121826] dark:text-[#DCE7F7] rounded-lg hover:bg-[#E8EDF5] dark:hover:bg-[#19335E] text-sm">{t('btn.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ NEW KEY DISPLAY MODAL ═══ */}
      {showNewKeyModal && newKeyData && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#0C1C38] rounded-2xl max-w-lg w-full p-6 border border-[#E8EDF5] dark:border-[#19335E] shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold text-[#0C1C38] dark:text-white">
                {newKeyData.kind === 'header' ? t('institution.mcp.keys.modal.createdTitleHeader') : t('institution.mcp.keys.modal.createdTitle')}
              </h3>
              <button type="button" onClick={() => { setShowNewKeyModal(false); setNewKeyData(null); }} className="p-1 hover:bg-[#F0F4FA] dark:hover:bg-[#122644] rounded-lg"><X className="w-5 h-5 text-[#5B6472]/70" /></button>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-700 dark:text-amber-300">
                    {newKeyData.kind === 'header' ? t('institution.mcp.keys.modal.secretWarningHeader') : t('institution.mcp.keys.modal.secretWarning')}
                  </div>
                </div>
              </div>
              {newKeyData.kind === 'client' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.fieldClientId')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-4 py-3 bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg font-mono text-sm text-[#121826] dark:text-[#DCE7F7]">{newKeyData.keyPrefix}</code>
                      <button type="button" onClick={() => copyToClipboard(newKeyData.keyPrefix, 'new-cid')} className="p-3 bg-[#0C1C38] text-white rounded-lg hover:bg-[#23867a] transition">
                        {copiedId === 'new-cid' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.fieldClientSecret')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-4 py-3 bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg font-mono text-sm text-[#121826] dark:text-[#DCE7F7] break-all">{newKeyData.key}</code>
                      <button type="button" onClick={() => copyToClipboard(newKeyData.key, 'new-secret')} className="p-3 bg-[#0C1C38] text-white rounded-lg hover:bg-[#23867a] transition">
                        {copiedId === 'new-secret' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.fieldHeaderName')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-4 py-3 bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg font-mono text-sm text-[#121826] dark:text-[#DCE7F7]">{newKeyData.headerName}</code>
                      <button type="button" onClick={() => copyToClipboard(newKeyData.headerName, 'new-hname')} className="p-3 bg-[#0C1C38] text-white rounded-lg hover:bg-[#23867a] transition">
                        {copiedId === 'new-hname' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5B6472] dark:text-[#DCE7F7] mb-1.5">{t('institution.mcp.keys.modal.fieldHeaderValue')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-4 py-3 bg-[#F8FAFC] dark:bg-[#122644] border border-[#E8EDF5] dark:border-[#19335E] rounded-lg font-mono text-sm text-[#121826] dark:text-[#DCE7F7] break-all">{newKeyData.secret}</code>
                      <button type="button" onClick={() => copyToClipboard(newKeyData.secret, 'new-hval')} className="p-3 bg-[#0C1C38] text-white rounded-lg hover:bg-[#23867a] transition">
                        {copiedId === 'new-hval' ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}
              <button type="button" onClick={() => { setShowNewKeyModal(false); setNewKeyData(null); }} className="w-full px-4 py-2.5 bg-[#0C1C38] hover:bg-[#23867a] text-white text-sm font-medium rounded-lg transition-all">{t('institution.mcp.keys.modal.doneButton')}</button>
            </div>
          </div>
        </div>
      )}
      <Modal
        open={Boolean(pendingDeleteKeyId)}
        onClose={() => {
          if (!deletingKey) setPendingDeleteKeyId(null);
        }}
        size="sm"
        closeOnBackdrop={!deletingKey}
      >
        <ModalHeader>
          <h3 className="text-base font-semibold text-[#0C1C38] dark:text-white">{t('institution.mcp.keys.modal.confirmDeleteTitle')}</h3>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-[#5B6472] dark:text-[#DCE7F7]">
            {t('institution.mcp.keys.modal.confirmDeleteBody')}
          </p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            onClick={() => setPendingDeleteKeyId(null)}
            disabled={deletingKey}
            className="px-4 py-2.5 bg-[#F0F4FA] dark:bg-[#122644] text-[#121826] dark:text-[#DCE7F7] rounded-lg hover:bg-[#E8EDF5] dark:hover:bg-[#19335E] text-sm disabled:opacity-50"
          >
            {t('btn.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void confirmDeleteKey()}
            disabled={deletingKey}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {deletingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('btn.delete')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
